import { Modal } from 'antd';
import { Formik } from 'formik';
import Form from 'formik-antd/lib/form';
import Select from 'formik-antd/lib/select';
import Button from 'jsx/common/Button';
import SubmitButton from 'jsx/common/SubmitButton';
import { filterOption } from 'jsx/common/util/filterOption';
import {
  Flex,
  FormGroup,
  ObjectGroups,
  ObjectGroupsIds,
  useAssetTranslation,
} from '@mss/mss-component-library';
import React from 'react';
import { ParentKey } from '../views/Users/Edit/ObjectPermissions';

const AUTHORIZATION_NS = { ns: 'authorization' as const };

type Props = {
  hideModal: Function;
  existingObjects: string[];
  unsaved: [ObjectGroupsIds, Function];
  groupedObjects: ObjectGroups;
  visible?: boolean;
  parentType?: ParentKey;
};

const AddObjectModal = ({
  hideModal,
  existingObjects,
  unsaved: [unsaved, set],
  visible = false,
  groupedObjects,
  parentType = 'user',
}: Props) => {
  const { t } = useAssetTranslation();

  const groupedAssets = groupedObjects.assets.filter(
    x => !existingObjects.includes(x.id)
  );
  const hasAssets = groupedAssets.length > 0;

  const groupedApps = groupedObjects.applications.filter(
    x => !existingObjects.includes(x.id)
  );
  const hasApps = groupedApps.length > 0;

  const hasAnyObjects = hasApps || hasAssets;

  const createInitial = (keys: string[]) => {
    let initial: ObjectGroupsIds = {};

    keys.forEach(key => {
      initial[key] =
        unsaved[key]?.filter(x => !existingObjects.includes(x)) || [];
    });

    return initial;
  };

  return (
    <Modal
      open={visible}
      footer={null}
      title={t(`Add object to ${parentType} permissions`, AUTHORIZATION_NS)}
      closable={false}
      destroyOnClose
    >
      {!hasAnyObjects && (
        <>
          <h4>{t('No more objects to add', AUTHORIZATION_NS)}</h4>
          <Flex h="end" className="mt-12">
            <Button onClick={hideModal}>{t('Close', AUTHORIZATION_NS)}</Button>
          </Flex>
        </>
      )}
      {hasAnyObjects && (
        <Formik
          onSubmit={async values => {
            await set(values);
            hideModal();
          }}
          initialValues={createInitial(['applications', 'assets'])}
        >
          {({ values }) => {
            return (
              <Form>
                {hasAssets && (
                  <FormGroup
                    label={t('Assets', AUTHORIZATION_NS)}
                    input={
                      <Select
                        value={values.assets}
                        name="assets"
                        mode="multiple"
                        allowClear
                        filterOption={filterOption}
                        style={{ width: '100%' }}
                        placeholder={t(
                          'Please select one or multiple assets to add',
                          AUTHORIZATION_NS
                        )}
                        getPopupContainer={trigger => trigger.parentNode}
                      >
                        {groupedAssets.map(x => (
                          <Select.Option value={x.id} key={x.id}>
                            {x.name}
                          </Select.Option>
                        ))}
                      </Select>
                    }
                  />
                )}
                {hasApps && (
                  <FormGroup
                    label={t('Applications', AUTHORIZATION_NS)}
                    input={
                      <Select
                        value={values.applications}
                        name="applications"
                        mode="multiple"
                        filterOption={filterOption}
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={t(
                          'Please select one or multiple applications to add',
                          AUTHORIZATION_NS
                        )}
                        getPopupContainer={trigger => trigger.parentNode}
                      >
                        {groupedApps.map(x => (
                          <Select.Option value={x.id} key={x.id}>
                            {x.name}
                          </Select.Option>
                        ))}
                      </Select>
                    }
                  />
                )}
                <Flex h="end" className="mt-12">
                  <Button onClick={hideModal} className="mr-2">
                    {t('Cancel', AUTHORIZATION_NS)}
                  </Button>
                  <SubmitButton>{t('Select', AUTHORIZATION_NS)}</SubmitButton>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default AddObjectModal;

